import { useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'

// Thin hook over AuthContext.api for /api/scheduler/*. Memoized so callers can list
// it as a useEffect dep (same reason as useTeamsDataApi).
export function useSchedulerApi() {
  const { api, apiRaw, apiKey } = useAuth()

  return useMemo(() => ({
    listSeasons() {
      return api('/scheduler/seasons')
    },
    createSeason({ label, league, sport, url, league_instance, subseason }) {
      return api('/scheduler/seasons', {
        method: 'POST',
        body: JSON.stringify({ label, league, sport, url, league_instance, subseason }),
      })
    },
    updateSeason(id, patch) {
      return api(`/scheduler/seasons/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
    },
    deleteSeason(id) {
      return api(`/scheduler/seasons/${id}`, { method: 'DELETE' })
    },
    listGames(id, { includeRemoved = false } = {}) {
      return api(`/scheduler/seasons/${id}/games${includeRemoved ? '?include_removed=1' : ''}`)
    },
    sync(id) {
      return api(`/scheduler/seasons/${id}/sync`, { method: 'POST' })
    },
    // -------- picks + catalog scheduling --------
    setPicks(seasonId, gameIds, picked) {
      return api(`/scheduler/seasons/${seasonId}/picks`, {
        method: 'POST',
        body: JSON.stringify({ game_ids: gameIds, picked }),
      })
    },
    scheduleToCatalog(seasonId, { mode, items, options }) {
      return api(`/scheduler/seasons/${seasonId}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ mode, items, options }),
      })
    },
    regenerate(seasonId, { items, update_start, duration_minutes }) {
      return api(`/scheduler/seasons/${seasonId}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ items, update_start, duration_minutes }),
      })
    },
    unlinkGame(gameId) {
      return api(`/scheduler/games/${gameId}/link`, { method: 'DELETE' })
    },

    // -------- templates (per league) --------
    listTemplates(league) {
      return api(`/scheduler/templates${league ? `?league=${encodeURIComponent(league)}` : ''}`)
    },
    createTemplate(t) {
      return api('/scheduler/templates', { method: 'POST', body: JSON.stringify(t) })
    },
    updateTemplate(id, patch) {
      return api(`/scheduler/templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
    },
    deleteTemplate(id) {
      return api(`/scheduler/templates/${id}`, { method: 'DELETE' })
    },

    // -------- team / league images (per league) --------
    listImages(league) {
      return api(`/scheduler/images${league ? `?league=${encodeURIComponent(league)}` : ''}`)
    },
    upsertImages(league, items) {
      return api('/scheduler/images', { method: 'PUT', body: JSON.stringify({ league, items }) })
    },
    deleteImage(id) {
      return api(`/scheduler/images/${id}`, { method: 'DELETE' })
    },

    // -------- thumbnail templates (per league) --------
    listThumbTemplates(league) {
      return api(`/scheduler/thumb-templates${league ? `?league=${encodeURIComponent(league)}` : ''}`)
    },
    createThumbTemplate(t) {
      return api('/scheduler/thumb-templates', { method: 'POST', body: JSON.stringify(t) })
    },
    updateThumbTemplate(id, patch) {
      return api(`/scheduler/thumb-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
    },
    deleteThumbTemplate(id) {
      return api(`/scheduler/thumb-templates/${id}`, { method: 'DELETE' })
    },
    // raw fetch (multipart) for image uploads — used by the thumbnail renderer
    apiRaw,

    // -------- catalog helpers for the schedule dialog --------
    listLiveInputs() { return api('/live-inputs') },
    listTags()       { return api('/tags') },
    listChannels()   { return api('/channels') },

    // Auth via ?key= so a plain <a href> download works (checkAuth accepts it).
    csvUrl(id) {
      const base = import.meta.env.VITE_API_BASE || '/api'
      return `${base}/scheduler/seasons/${id}/games.csv${apiKey ? `?key=${encodeURIComponent(apiKey)}` : ''}`
    },
  }), [api, apiRaw, apiKey])
}
